import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {
  Bar,
  BarChart,
  CartesianGrid,
  DefaultZIndexes,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  usePlotArea,
  useYAxisScale,
  XAxis,
  XAxisTickContentProps,
  YAxis,
  ZIndexLayer,
} from "recharts"

import {GenericErrorAlert} from "../../../components/Alert"
import {Loading} from "../../../components/Loading"
import {TReachRange, useReachOutreachDepthQuery} from "../../../queries/reachEngine"
import {AOutreachDepthListData} from "../../../services/types.generated"
import {useResizeRef} from "../../../utils/useResizeRef"
import {DashboardCard} from "./DashboardCard"
import {EmptyState} from "./EmptyState"
import {
  CHART_AXIS_STROKE,
  CHART_GRID_STROKE,
  CHART_LABEL_FILL,
  CHART_LABEL_FONT_SIZE,
  CHART_VALUE_TICK,
  formatAxisTick,
  SQL_COLOR,
  SqlMqlTooltip,
} from "./ResultsChart"

type TBucket = AOutreachDepthListData["buckets"][number]

// Concrete colour: the tooltip cursor is SVG paint, where CSS variables don't resolve.
const CURSOR_FILL = "rgb(243, 245, 251)" // --color-cr-slate-2

const isEmptyDepth = (buckets: readonly TBucket[]) => buckets.every(bucket => bucket.sql === 0)

const CHART_MARGIN = {top: 8, right: 16, left: 0, bottom: 0}
const Y_AXIS_WIDTH = 48
// Gap between the top of a bar and the baseline of its count, as in the design.
const COUNT_GAP = 7
// Room above the count's baseline for its glyphs' ascent.
const COUNT_ASCENT = 13
// A generous average glyph width at the count's font size, used to tell whether a count fits its column.
const CHAR_WIDTH = 7

const compactNumber = new Intl.NumberFormat(undefined, {notation: "compact", maximumFractionDigits: 1})

// How the count fits above a bar: the full number, or abbreviated ("1.2M SQL") when a column is too narrow.
type TCountFit = "full" | "abbreviated"

const formatCountFor = (fit: TCountFit) => (value: number) =>
  fit === "abbreviated" ? compactNumber.format(value) : value.toLocaleString()

// The tick index is the bucket's index (interval={0} gives one tick per bucket); unlike the label,
// it stays unambiguous when two buckets share a label.
const bucketOfTick = (buckets: readonly TBucket[], tick: {index?: number; value?: unknown}) =>
  typeof tick.index === "number" ? buckets[tick.index] : buckets.find(bucket => bucket.label === tick.value)

type TDepthTickProps = XAxisTickContentProps & {buckets: readonly TBucket[]}

const DepthTick: React.FC<TDepthTickProps> = ({x, y, payload, className, buckets}) => {
  const {t} = useTranslation()
  const bucket = bucketOfTick(buckets, payload)
  if (!bucket) {
    return null
  }

  return (
    <text
      className={className}
      x={x}
      y={y}
      dy={"0.71em"}
      textAnchor={"middle"}
      fontSize={CHART_LABEL_FONT_SIZE}
      fontWeight={600}
      fill={CHART_LABEL_FILL}
    >
      {t("ReachEngine_OutreachDepth_Bucket", {label: bucket.label})}
    </text>
  )
}

// Each bucket's SQL count, centred above its bar. The counts are placed from the plot area and the value
// scale rather than a LabelList, because Recharts drops zero-height bars (and their labels). The category
// axis splits the plot into equal bands, one per bucket in order, so a bucket's centre follows from its
// index. (useXAxisTicks can't be used here: under StrictMode its state is left empty after the effects re-run.)
// They paint in the label layer, above the tooltip cursor that highlights the active bucket.
const BarCounts: React.FC<{buckets: readonly TBucket[]; fit: TCountFit}> = ({buckets, fit}) => {
  const plot = usePlotArea()
  const yScale = useYAxisScale()
  if (!plot || !yScale || buckets.length === 0) {
    return null
  }
  const formatCount = formatCountFor(fit)
  const band = plot.width / buckets.length

  return (
    <ZIndexLayer zIndex={DefaultZIndexes.label}>
      <g className={"outreach-depth-counts"}>
        {buckets.map((bucket, bucketIndex) => {
          const barTop = yScale(bucket.sql)
          if (barTop === undefined) {
            return null
          }
          return (
            <text
              key={bucketIndex}
              className={"outreach-depth-count"}
              x={plot.x + (bucketIndex + 0.5) * band}
              y={barTop - COUNT_GAP}
              textAnchor={"middle"}
              fontSize={CHART_LABEL_FONT_SIZE}
              fill={CHART_LABEL_FILL}
            >
              <Trans
                i18nKey={"ReachEngine_OutreachDepth_SqlCount"}
                values={{value: formatCount(bucket.sql)}}
                components={{value: <tspan fontSize={13} fontWeight={700} fill={SQL_COLOR} />}}
              />
            </text>
          )
        })}
      </g>
    </ZIndexLayer>
  )
}

const OutreachDepthChart: React.FC<{buckets: readonly TBucket[]}> = ({buckets}) => {
  const {t} = useTranslation()
  const [chartWidth, setChartWidth] = React.useState(0)
  const chartRef = useResizeRef(element => setChartWidth(element.getBoundingClientRect().width))

  const column = (chartWidth - CHART_MARGIN.left - CHART_MARGIN.right - Y_AXIS_WIDTH) / Math.max(buckets.length, 1)
  // Estimates a full-length translated count's width, its markup tags stripped.
  const fullCountWidth = (sql: number) =>
    t("ReachEngine_OutreachDepth_SqlCount", {value: sql.toLocaleString()}).replace(/<\/?\w+>/g, "").length * CHAR_WIDTH
  const fit: TCountFit = buckets.every(({sql}) => fullCountWidth(sql) <= column) ? "full" : "abbreviated"
  // Headroom above the plot so even the tallest bar's count stays inside the chart.
  const margin = {...CHART_MARGIN, top: COUNT_ASCENT + COUNT_GAP}

  const renderTick = React.useCallback(
    (props: XAxisTickContentProps) => <DepthTick {...props} buckets={buckets} />,
    [buckets]
  )

  const renderTooltip = React.useCallback(
    ({active, payload}: TooltipContentProps) => {
      if (!active || !payload?.length) {
        return null
      }
      const bucket = payload[0].payload as TBucket
      return (
        <SqlMqlTooltip
          heading={t("ReachEngine_OutreachDepth_Bucket", {label: bucket.label})}
          sql={Number(payload.find(item => item.dataKey === "sql")?.value ?? 0)}
        />
      )
    },
    [t]
  )

  return (
    <div ref={chartRef} className={"w-full"} style={{height: 240}} data-testid={"outreach-depth-chart"}>
      <ResponsiveContainer width={"100%"} height={"100%"}>
        <BarChart
          data={buckets}
          margin={margin}
          // Recharts insets each bar by this gap on both sides of its band, so neighbouring
          // bars sit 16px apart and each one fills the rest of its bucket column.
          barCategoryGap={8}
          aria-label={t("ReachEngine_OutreachDepth_Title")}
        >
          <CartesianGrid stroke={CHART_GRID_STROKE} vertical={false} />
          <XAxis
            dataKey={"label"}
            interval={0}
            tickLine={false}
            tickSize={0}
            tickMargin={9}
            axisLine={{stroke: CHART_AXIS_STROKE}}
            tick={renderTick}
          />
          <YAxis
            allowDecimals={false}
            width={Y_AXIS_WIDTH}
            tickFormatter={formatAxisTick}
            tickLine={false}
            axisLine={false}
            tick={CHART_VALUE_TICK}
          />
          <Tooltip content={renderTooltip} cursor={{fill: CURSOR_FILL}} />
          <Bar
            dataKey={"sql"}
            fill={SQL_COLOR}
            radius={[4, 4, 0, 0]}
            animationDuration={600}
            animationEasing={"ease-in-out"}
          />
          <BarCounts buckets={buckets} fit={fit} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export const OutreachDepthCard: React.FC<{range: TReachRange}> = ({range}) => {
  const {t} = useTranslation()
  const {data: buckets, error, isLoading, refetch} = useReachOutreachDepthQuery(range)

  return (
    <DashboardCard title={t("ReachEngine_OutreachDepth_Title")} subtitle={t("ReachEngine_OutreachDepth_Subtitle")}>
      <div className={"mt-[18px]"}>
        {isLoading ? (
          <div className={"h-60"}>
            <Loading size={"xl"} />
          </div>
        ) : error || !buckets ? (
          <GenericErrorAlert retry={refetch} />
        ) : isEmptyDepth(buckets) ? (
          <EmptyState className={"h-60"} />
        ) : (
          <OutreachDepthChart buckets={buckets} />
        )}
      </div>
    </DashboardCard>
  )
}
